import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  withCredentials: true
});

// Attach JWT to every request
api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Redirect to login on 401
api.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (name, email, password) => api.post('/auth/register', { name, email, password }),
  me: () => api.get('/auth/me')
};

export const adminAPI = {
  getConfig: () => api.get('/admin/config'),
  updateConfig: (payload) => api.put('/admin/config', payload),
  listUsers: () => api.get('/admin/users'),
  createUser: (payload) => api.post('/admin/users', payload),
  updateUser: (id, payload) => api.put(`/admin/users/${id}`, payload),
  approveUser: (id) => api.put(`/admin/users/${id}`, { approval_status: 'approved' }),
  rejectUser: (id) => api.put(`/admin/users/${id}`, { approval_status: 'rejected' }),
  resetPassword: (id, password) => api.put(`/admin/users/${id}/reset-password`, { password }),
  getUsageAnalytics: () => api.get('/admin/analytics'),
  getQueryHistory: (params = {}) => api.get('/admin/query-history', { params }),
  getSecurityConfig: () => api.get('/admin/security'),
  updateSecurityConfig: (payload) => api.put('/admin/security', payload),
  getColumnAliases: () => api.get('/admin/schema/column-aliases'),
  upsertColumnAlias: (raw_name, display_name, intent) => api.put('/admin/schema/column-aliases', { raw_name, display_name, intent }),
  deleteColumnAlias: (raw_name) => api.delete(`/admin/schema/column-aliases/${encodeURIComponent(raw_name)}`),
  getTables: () => api.get('/admin/schema/tables'),
  updateTables: (tables) => api.put('/admin/schema/tables', { tables }),
  tableDefs: {
    list:           (module) => api.get('/admin/table-definitions', { params: module ? { module } : {} }),
    get:            (name)   => api.get(`/admin/table-definitions/${encodeURIComponent(name)}`),
    create:         (payload) => api.post('/admin/table-definitions', payload),
    update:         (name, payload) => api.put(`/admin/table-definitions/${encodeURIComponent(name)}`, payload),
    delete:         (name)   => api.delete(`/admin/table-definitions/${encodeURIComponent(name)}`),
    columnsPreview: (name)   => api.get(`/admin/table-definitions/${encodeURIComponent(name)}/columns-preview`),
  },
  dbConfig: {
    get:    ()       => api.get('/admin/db-config'),
    test:   (payload) => api.post('/admin/db-config/test', payload),
    update: (payload) => api.put('/admin/db-config', payload),
  },
  getCompanyProfile: () => api.get('/admin/company-profile'),
  updateCompanyProfile: (payload) => api.put('/admin/company-profile', payload),
  modules: {
    list:          ()           => api.get('/admin/modules'),
    create:        (payload)    => api.post('/admin/modules', payload),
    update:        (id, payload) => api.put(`/admin/modules/${id}`, payload),
    delete:        (id)         => api.delete(`/admin/modules/${id}`),
    suggestTables: (payload)    => api.post('/admin/modules/suggest-tables', payload),
  },
  columnEnums: {
    list:     (table)   => api.get('/admin/column-enums', { params: table ? { table } : {} }),
    upsert:   (payload) => api.put('/admin/column-enums', payload),
    delete:   (id)      => api.delete(`/admin/column-enums/${id}`),
    discover: ()        => api.post('/admin/column-enums/discover'),
  },
  playground: {
    test: (query) => api.post('/admin/playground/test', { query }),
  },
  telegram: {
    getConfig:  ()        => api.get('/admin/telegram'),
    setToken:   (token)   => api.put('/admin/telegram', { token }),
    stopBot:    ()        => api.delete('/admin/telegram'),
  },
  whatsapp: {
    getConfig:  ()        => api.get('/admin/whatsapp'),
    configure:  (payload) => api.put('/admin/whatsapp', payload),
    disable:    ()        => api.delete('/admin/whatsapp'),
  },
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  getSemantic: () => api.get('/admin/schema/semantic'),
  updateSemantic: (semantic) => api.put('/admin/schema/semantic', { semantic }),
  generateSemantic: () => api.post('/admin/schema/semantic/generate'),
  uploadSchema: (file) => { const fd = new FormData(); fd.append('file', file); return api.post('/admin/schema/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); },
  commitSchema: (tables, semantic) => api.post('/admin/schema/commit', { tables, semantic }),
  exportSchema: () => api.get('/admin/schema/export', { responseType: 'blob' }),
  importSchema: (file) => { const fd = new FormData(); fd.append('file', file); return api.post('/admin/schema/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); },
};

export const queryAPI = {
  run: (query, conversationHistory = []) => api.post('/query', { query, conversationHistory }),
  analytics: (table = 'OSQ', queryText = '') => api.post('/query', { query: queryText || 'sales analysis', analyticsTable: table }),
  history: (limit = 20) => api.get(`/query/history?limit=${limit}`),
  saved: () => api.get('/query/saved'),
  save: (name, query, sql) => api.post('/query/save', { name, query, sql }),
  schema: () => api.get('/query/schema'),
  suggestions: () => api.get('/query/suggestions')
};

export const chatAPI = {
  getHistory: () => api.get('/chat/history'),
  clearHistory: () => api.delete('/chat/history')
};

export const contactAPI = {
  lookup: (q) => api.get('/contact/lookup', { params: { q } })
};

export const exportAPI = {
  pdf: (payload) => api.post('/export/pdf', payload, { responseType: 'blob' }),
  excel: (payload) => api.post('/export/excel', payload, { responseType: 'blob' }),
  analyticsExcel: (payload) => api.post('/export/analytics-excel', payload, { responseType: 'blob' }),
  analyticsHTML:  (payload) => api.post('/export/analytics-html',  payload, { responseType: 'blob' })
};

export default api;
